import React from 'react';
import styled from 'styled-components';

import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';

import { usePaginatedFetch } from '../../../hooks/usePaginatedFetch';
import { UamAuditEntry } from '../../../trustAdmin-api/generated-src';
import TrustAdminApiFactory from '../../../trustAdmin-api/TrustAdminApiFactory';
import { formatTimestamp } from '../../../utils/formatTimestamp';
import { RequestErrorCard } from '../error/RequestErrorCard';

export interface UamAuditHistoryProps {
    tconst: string;
}

const INITIAL_VISIBLE_COUNT = 5;

export const UamAuditHistory: React.FC<UamAuditHistoryProps> = ({ tconst }) => {
    const [showAll, setShowAll] = React.useState<boolean>(false);

    const {
        items: auditEntries,
        nextToken,
        isInitialLoading,
        isLoadingMore,
        error,
        loadMore
    } = usePaginatedFetch<UamAuditEntry>(
        (token) => TrustAdminApiFactory().unusualActivity.getUnusualActivityHistory(tconst, undefined, token),
        (data) => data.auditHistory,
        []
    );

    const handleLoadMore = () => {
        setShowAll(true);
        loadMore();
    };

    if (isInitialLoading) {
        return (
            <Column alignmentHorizontal='center' spacingInset='300'>
                <Loader size='medium' data-testid='uam-inline-history-loader' />
            </Column>
        );
    }

    // Derive error visibility: hide the error card while a retry is in-flight
    // so the stale error doesn't flash alongside the loader.
    const shouldShowError = error && !isLoadingMore;

    if (shouldShowError && auditEntries.length === 0) {
        return <RequestErrorCard error={error} />;
    }

    if (auditEntries.length === 0) {
        return (
            <Text type='b200' color='secondary' data-testid='uam-inline-history-empty'>
                No audit history found for this title.
            </Text>
        );
    }

    const visibleEntries = showAll ? auditEntries : auditEntries.slice(0, INITIAL_VISIBLE_COUNT);
    const hasMore = auditEntries.length > INITIAL_VISIBLE_COUNT;

    return (
        <Column spacing='200' data-testid='uam-inline-history'>
            <Text type='h100'>Audit History</Text>
            <TableBorder>
                <Table headerRows={1} spacing='small' showDividers={true} showStripes={true} layout='fixed'>
                    <TableRow>
                        <TableCell width='12%'>Operation</TableCell>
                        <TableCell width='12%'>Admin</TableCell>
                        <TableCell width='22%'>Last Updated</TableCell>
                        <TableCell width='54%'>Note</TableCell>
                    </TableRow>
                    {visibleEntries.map((entry, index) => (
                        <TableRow key={`${entry.updatedAt}-${index}`} data-testid={`uam-inline-history-row-${index}`}>
                            <TableCell>
                                <Tag type={entry.operation === 'ADD' ? 'success' : 'error'}>{entry.operation}</Tag>
                            </TableCell>
                            <TableCell>{entry.adminAlias}</TableCell>
                            <TableCell>{formatTimestamp(entry.updatedAt)}</TableCell>
                            <TableCell>
                                <span style={{ whiteSpace: 'pre-wrap' }}>{entry.note}</span>
                            </TableCell>
                        </TableRow>
                    ))}
                </Table>
            </TableBorder>
            <Row alignmentHorizontal='center' spacing='300'>
                {hasMore && !showAll && (
                    <Button type='tertiary' onClick={() => setShowAll(true)} data-testid='uam-inline-history-show-all'>
                        {`Show all ${auditEntries.length} entries`}
                    </Button>
                )}
                {/* Show Load More when all loaded entries are visible (either via "Show all"
                   or when total entries ≤ INITIAL_VISIBLE_COUNT) and more pages exist */}
                {(showAll || !hasMore) && nextToken && (
                    <Button
                        type='secondary'
                        onClick={handleLoadMore}
                        disabled={isLoadingMore}
                        data-testid='uam-inline-history-load-more'
                    >
                        {isLoadingMore ? <Loader type='linear' size='small' /> : 'Load More'}
                    </Button>
                )}
            </Row>
            {shouldShowError && auditEntries.length > 0 && <RequestErrorCard error={error} />}
        </Column>
    );
};

const TableBorder = styled.div`
    border: 1px solid #d5dbdb;
    border-radius: 4px;
    overflow: hidden;
    width: 85%;
`;
